/**
 * One bucket-group section of the Budget month view (Phase 3, §6.2): a group
 * header with its budgeted/spent subtotal, then a `BudgetRow` per non-excluded
 * bucket. Excluded / Transfer buckets are never rendered (§6.2).
 */
import { money } from '../../lib/format.ts';
import type { BucketGroupWithBuckets, MonthBucketRow } from '../../lib/types.ts';
import { BudgetRow } from './BudgetRow.tsx';

export interface BudgetGroupSectionProps {
  group: BucketGroupWithBuckets;
  rowsByBucket: Map<number, MonthBucketRow>;
  disabled?: boolean;
  onSetBudget: (bucketId: number, amount: string | null) => void;
}

export function BudgetGroupSection({
  group,
  rowsByBucket,
  disabled,
  onSetBudget,
}: BudgetGroupSectionProps) {
  const buckets = group.buckets.filter((b) => !b.excluded_from_reports && !b.is_transfer);
  if (buckets.length === 0) return null;

  let budgetedSubtotal = 0;
  let spentSubtotal = 0;
  for (const bucket of buckets) {
    const row = rowsByBucket.get(bucket.id);
    if (!row) continue;
    if (row.budgeted !== null) budgetedSubtotal += Number.parseFloat(row.budgeted) || 0;
    const spent = Number.parseFloat(row.spent) || 0;
    if (spent > 0) spentSubtotal += spent;
  }

  return (
    <section>
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2.5 dark:border-slate-800/70">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
          {group.icon ? <span aria-hidden>{group.icon}</span> : null}
          {group.name}
        </h2>
        <span className="text-xs text-slate-500 tabular-nums dark:text-slate-400">
          {money(spentSubtotal)} / {money(budgetedSubtotal)}
        </span>
      </header>
      <div className="divide-y divide-slate-100 dark:divide-slate-800/70">
        {buckets.map((bucket) => (
          <BudgetRow
            key={bucket.id}
            bucket={bucket}
            row={rowsByBucket.get(bucket.id)}
            disabled={disabled}
            archived={bucket.archived || group.archived}
            onSetBudget={onSetBudget}
          />
        ))}
      </div>
    </section>
  );
}
